import type { Map as MapLibreMap } from 'maplibre-gl'

import { fetchPointSeries, findVariable } from '../../api/models'
import type { Layer } from '../layer'
import type { ModelLayer } from './modelLayer'
import { describeCell, RadarMapLayer } from './radarMapLayer'
import {
  parseFeatureInfo,
  parseRasterValue,
  queryForClick,
  featureInfoUrl,
  type AlertFeature,
} from './featureInfo'
import {
  ALERTS_ID,
  ALERTS_TARGET,
  getLayerDef,
  MD_ID,
  type LayerDef,
  type Readout,
} from './registry'
import type { MdProps, OutlookProps } from './spc'

/**
 * What the active overlays say about one point on the map.
 *
 * Raster layers are asked in parallel with the same pixel and with the `TIME`
 * *that layer is drawing* — not the slider's position. Those differ: a layer
 * snaps the shared axis position to its own nearest frame, and GeoServer's
 * `nearestValue` snapping has a tolerance rather than reaching arbitrarily
 * far. Measured 2026-09-17: NDFD temperature answers for a TIME 30 minutes off
 * its grid and returns an empty FeatureCollection for one 68 minutes off,
 * while the tiles on screen were drawing its own nearest hour the whole time.
 * Asking with the layer's own instant is the only way the number matches the
 * pixel.
 *
 * Vector layers skip all of that. Their geometry is already in the browser, so
 * the answer comes out of `queryRenderedFeatures` with no request, no `TIME`
 * and none of the pixel arithmetic above.
 *
 * A layer that fails or has nothing there reports `null` rather than dropping
 * out of the list: "Radar —" is information, and a row that vanishes looks
 * like a bug.
 */

export interface LayerReadout {
  id: string
  label: string
  /** Raster layers. null: outside coverage, no data, or the query failed. */
  value: number | null
  /**
   * The point is inside the field and the model says there is nothing there —
   * below the plotted floor, or exactly the nodata sentinel.
   *
   * A *different* answer from `value: null` and this absent, which means we
   * have no idea: someone who taps the Pacific should not be told there is no
   * echo there. The API distinguishes them, so the menu can too.
   *
   * Optional because only a model readout can be sure: a layer with no floor
   * never sets it, and "not stated" is the same as false.
   */
  belowFloor?: boolean
  /**
   * The point is outside a *cropped* model's stored area.
   *
   * The fourth answer, and it only exists for a model that declares a crop:
   * GFS is stored on a CONUS window of a global grid, so "—" for a tap over
   * the Atlantic is correct and tells nobody anything. Same discipline as
   * `belowFloor`: the API knows which it is, so the menu says which it is.
   */
  outsideArea?: boolean
  /** Categorical layers, in the product's own words: "Marginal Risk". null
   *  when the point is outside every area the layer draws. */
  text: string | null
  units: string
  decimals: number
  /** The frame this reading came from; null for a time-invariant layer. */
  time: Date | null
  /** Products with a validity window rather than an instant on the axis. */
  valid: { from: Date; to: Date } | null
}

/** One mesoscale discussion under the finger. */
export interface MdHit {
  /** Four digits, zero-padded, as `mdTextUrl` wants it. */
  number: string
  label: string
  /** The service's own display string: "MD 2304 Active Till 2215 UTC". */
  info: string
}

export interface PointProbe {
  readouts: LayerReadout[]
  alerts: AlertFeature[]
  mds: MdHit[]
}

function inRange(readout: Extract<Readout, { kind: 'raster' }>, value: number): boolean {
  const range = readout.range
  return !range || (value >= range[0] && value <= range[1])
}

/** The fill a `GeoJsonLayer` draws under `id`; `geoJsonLayer.ts` names it. */
function fillLayerId(id: string): string {
  return `wx:${id}:fill`
}

/**
 * A vector layer's answer, straight out of the browser.
 *
 * `queryRenderedFeatures` excludes a layer at `visibility: none` but *includes*
 * one at opacity 0. The controller already turns opacity 0 into
 * `visibility: none`, so a layer dragged to invisible correctly stops
 * answering — which is one more reason that is the right rule.
 */
function readFeature(
  map: MapLibreMap,
  def: LayerDef,
  point: { x: number; y: number },
  base: LayerReadout,
): LayerReadout {
  let hits: OutlookProps[]
  try {
    hits = map
      .queryRenderedFeatures(point as never, { layers: [fillLayerId(def.id)] })
      .map((f) => f.properties as unknown as OutlookProps)
  } catch {
    // The style layer is not mounted — mid basemap switch, most likely.
    return base
  }
  // Highest rank wins: a 15 % area inside a 5 % area is the answer.
  const top = hits.filter((p) => p && typeof p.rank === 'number').sort((a, b) => b.rank - a.rank)[0]
  if (!top) return base
  return {
    ...base,
    text: top.full || top.short,
    valid:
      Number.isFinite(top.from) && Number.isFinite(top.to)
        ? { from: new Date(top.from), to: new Date(top.to) }
        : null,
  }
}

async function readRaster(
  map: MapLibreMap,
  readout: Extract<Readout, { kind: 'raster' }>,
  point: { x: number; y: number },
  base: LayerReadout,
): Promise<LayerReadout> {
  try {
    const url = featureInfoUrl({
      ...queryForClick(map, readout.target, point, base.time),
      featureCount: 1,
    })
    const resp = await fetch(url)
    if (!resp.ok) return base
    const value = parseRasterValue(await resp.json())
    return { ...base, value: value !== null && inRange(readout, value) ? value : null }
  } catch {
    return base
  }
}

/**
 * Our own model field, out of `/api/point`.
 *
 * Asked with the forecast hour the layer is *drawing* — `ModelLayer`'s own
 * `currentFhr()`, resolved through the manifest — never the slider's index.
 * The WMS branch above needs the same discipline for a different reason
 * (GeoServer's `nearestValue` tolerance); here the mapping is exact, because
 * the frames are ours, and the rule holds for the same reason: the number has
 * to describe the pixel on screen.
 *
 * Units and decimals come from the catalog rather than from the registry row,
 * because the backend registry is what coloured the pixels.
 */
async function readModel(
  readout: Extract<Readout, { kind: 'model' }>,
  layer: Layer | undefined,
  lat: number,
  lon: number,
  base: LayerReadout,
): Promise<LayerReadout> {
  const model = layer as ModelLayer | undefined
  const found = await findVariable(readout.model, readout.variable)
  const withUnits: LayerReadout = found
    ? { ...base, units: found.variable.units, decimals: found.variable.decimals }
    : base

  const lead = model?.currentLead?.() ?? undefined
  const run = model?.currentRun?.() ?? undefined
  if (lead === null || lead === undefined || run === undefined) return withUnits

  const series = await fetchPointSeries(readout.model, readout.variable, lat, lon, { run, lead })
  const step = series?.steps?.[0]
  if (!step) return withUnits
  return {
    ...withUnits,
    value: typeof step.value === 'number' ? step.value : null,
    belowFloor: step.below_floor === true,
    // Outside a cropped model's stored area there is no value *and* no "no
    // echo" — "—" is correct and unhelpful, so the menu gets to say which.
    outsideArea: series?.grid === null && series?.cropped === true,
  }
}

/**
 * The radar map, out of the plate on screen: the cell under the point, in the
 * "Next hour" section's words, and said to be a forecast when the frame is
 * past the newest radar. No request: the plate is already decoded in the page.
 * Beyond the radars is "—", never "No precipitation".
 */
function readNowcast(layer: Layer | undefined, lat: number, lon: number, base: LayerReadout) {
  if (!(layer instanceof RadarMapLayer)) return base
  const cell = layer.cellAt(lon, lat)
  if (!cell) return base
  const until = layer.observedUntil()
  const shown = layer.currentTime()
  const forecast = !!until && !!shown && shown.getTime() > until.getTime()
  return { ...base, text: describeCell(cell, layer.bands(), forecast) }
}

async function readLayer(
  map: MapLibreMap,
  def: LayerDef,
  point: { x: number; y: number },
  time: Date | null,
  context: ProbeContext,
): Promise<LayerReadout> {
  const base: LayerReadout = {
    id: def.id,
    label: def.label,
    value: null,
    text: null,
    units: def.units,
    decimals: def.readout?.kind === 'raster' ? def.readout.decimals : 0,
    time,
    valid: null,
  }
  switch (def.readout?.kind) {
    case 'raster':
      return readRaster(map, def.readout, point, base)
    case 'feature':
      return readFeature(map, def, point, base)
    case 'model':
      return readModel(def.readout, context.layers?.get(def.id), context.lat, context.lon, base)
    case 'nowcast':
      return readNowcast(context.layers?.get(def.id), context.lat, context.lon, base)
    default:
      return base
  }
}

/**
 * What a model readout needs and a raster one does not: the point in degrees
 * (our own API works in lat/lon, not in screen pixels) and the live `Layer`
 * objects, because only the layer knows which frame it is actually drawing.
 */
export interface ProbeContext {
  lat: number
  lon: number
  layers?: ReadonlyMap<string, Layer>
}

/**
 * Probe every active layer at `point`. `activeIds` is bottom-first stack
 * order, so the list is reversed: the menu reads top-down like the map does.
 * `times` is each layer's own current frame, from `LayerController`.
 */
export async function probePoint(
  map: MapLibreMap,
  activeIds: readonly string[],
  point: { x: number; y: number },
  times: ReadonlyMap<string, Date | null>,
  context: ProbeContext,
): Promise<PointProbe> {
  const defs = [...activeIds]
    .reverse()
    .map(getLayerDef)
    .filter((d): d is LayerDef => !!d)

  const wantsAlerts = defs.some((d) => d.id === ALERTS_ID)
  const [readouts, alerts] = await Promise.all([
    Promise.all(
      defs
        .filter((d) => d.readout)
        .map((d) => readLayer(map, d, point, times.get(d.id) ?? null, context)),
    ),
    wantsAlerts ? readAlerts(map, point) : Promise.resolve([]),
  ])
  // No await: the MD geometry is already in the browser.
  const mds = defs.some((d) => d.id === MD_ID) ? readMds(map, point) : []
  return { readouts, alerts, mds }
}

async function readAlerts(
  map: MapLibreMap,
  point: { x: number; y: number },
): Promise<AlertFeature[]> {
  try {
    const resp = await fetch(featureInfoUrl(queryForClick(map, ALERTS_TARGET, point)))
    if (!resp.ok) return []
    return parseFeatureInfo(await resp.json())
  } catch {
    return []
  }
}

/**
 * The discussions covering the point.
 *
 * MDs draw as an outline, but their fill layer is mounted at opacity 0 as the
 * hit target — `queryRenderedFeatures` includes it, and requiring the finger
 * to land on a 2 px stroke would make an MD unopenable on a phone. Duplicates
 * are possible when two discussions overlap, so they are keyed by number.
 */
function readMds(map: MapLibreMap, point: { x: number; y: number }): MdHit[] {
  const out: MdHit[] = []
  const seen = new Set<string>()
  let features: MdProps[]
  try {
    features = map
      .queryRenderedFeatures(point as never, { layers: [fillLayerId(MD_ID)] })
      .map((f) => f.properties as unknown as MdProps)
  } catch {
    return []
  }
  for (const p of features) {
    if (!p?.number || seen.has(p.number)) continue
    seen.add(p.number)
    out.push({ number: p.number, label: p.short, info: p.full })
  }
  return out
}

/**
 * "25 dBZ", "72 °F", "0.04 in", "Slight Risk". Units with no space: percent
 * reads better tight.
 *
 * **Between cell centres, this number and the colour under the cursor no
 * longer agree, and that is deliberate.** Until Phase 7 a model plate drew
 * `nearest`, so a screen pixel *was* one grid cell and `/api/point` confirmed
 * it exactly. Every field draws smooth now, so the colour between two cell
 * centres is an interpolation while this — which reads Zarr — returns the
 * cell. At CONUS zoom nobody can see the difference; at z12 on a 3 km HRRR
 * plate a screen pixel is a fraction of a cell and somebody will notice.
 *
 * Three things hold it together, and this is the place a reader goes to ask
 * what a number means, so they are written here:
 *
 * * **The peak is exact.** A bilinear filter reproduces texel-centre values
 *   exactly and only interpolates *between* them — measured at a real
 *   convective core as 0.2 dBZ at 2048 px and nothing at all at 2816, against
 *   a render width that was costing 2.3 dBZ before Stage 6.2 fixed it.
 * * **This readout stays the authority.** It was deliberately left on
 *   `/api/point` rather than moved into the shader, where it would have agreed
 *   with the pixels by agreeing with the interpolation. It is also finer: the
 *   texture holds the ramp's 0.6 °F where Zarr holds `store_decimals`.
 * * **It is not Stage 6.2's bug wearing a new hat.** There the plate was
 *   *missing* a cell the grid held. Here every cell is present and what is
 *   drawn between them is stated to be a blend.
 */
export function formatReadout(readout: LayerReadout): string {
  if (readout.text) return readout.text
  // Three answers for a model, four for a cropped one, and a sentence each.
  // "No echo" is not the same as "—": one says the model has nothing there,
  // the other says we have nothing to say — and someone who taps the Pacific
  // should get the second. "Outside this model" is the third of those: for
  // GFS, stored on a CONUS window of a global grid, most of the map is outside
  // and "—" would suggest a failure. A nodata sentinel is an ordinary number
  // and printing it is a fabrication.
  //
  // The wording is reflectivity's, which is the only field with a floor today.
  // A field whose floor means something else ("no snow", "calm") needs its own
  // word, and that word belongs in the backend registry beside the floor.
  if (readout.value === null) {
    if (readout.belowFloor) return 'No echo'
    return readout.outsideArea ? 'Outside this model' : '—'
  }
  const value = readout.value.toFixed(readout.decimals)
  if (!readout.units) return value
  return readout.units === '%' ? `${value}%` : `${value} ${readout.units}`
}
